import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import ExternalShippingJobTooltip from '../common-comps/ExternalShippingJobsTooltip';
import { DOCK_TYPE_UBER } from '../apps/dispatch/utils/dock_n_driver';

/**
 * Formate la fenêtre de livraison (ready - deadline) au format LT (heure locale).
 * @param {Object} batch - L'objet uberdirect_batch
 * @returns {string} La fenêtre formatée ou une chaîne vide
 */
const formatDropoffWindow = (batch) => {
  if (batch && (!batch.common_dropoff_ready || !batch.common_dropoff_deadline)) {
    return '';
  }
  const ready = moment(batch.common_dropoff_ready).format('LT');
  const deadline = moment(batch.common_dropoff_deadline).format('LT');
  return `${ready} - ${deadline}`;
};

/**
 * Génère le libellé de statut pour un batch UberDirect.
 * @param {Object} dock - L'objet dock
 * @param {Object} batch - L'objet uberdirect_batch
 * @returns {string} Le libellé de statut
 */
const getBatchStatusLabel = (dock, batch) => {
  const windowLabel = formatDropoffWindow(batch);

  switch (batch.status) {
    case 'draft':
      return windowLabel ? `Attente (${windowLabel})` : 'Attente';

    case 'processing':
      return windowLabel ? `Traitement par UD... (${windowLabel})` : 'traitement par UD...';

    case 'active':
      // Si gone_at est défini, le dock est en cours de livraison
      if (dock.gone_at) {
        return 'Livraison';
      }
      // Sinon, afficher les infos du coursier depuis external_job
      if (dock.external_job && dock.external_job.id) {
        return getExternalJobStatusLabel(dock.external_job, dock.gone_at);
      }
      return windowLabel ? `Actif (${windowLabel})` : 'Actif'

    case 'completed':
      return 'Terminé';

    case 'failed':
      if (batch.failed_orders && batch.failed_orders.length > 0) {
        return `ECHEC (${batch.failed_orders.length} commande(s) en échec)`;
      }
      return 'ECHEC';

    default:
      return batch.status || 'Inconnu';
  }
};

/**
 * Génère le libellé de statut pour un external_job (job Uber individuel).
 * @param {Object} externalJob - L'objet external_job
 * @param {string} goneAt - La date de départ du dock
 * @returns {string} Le libellé de statut
 */
const getExternalJobStatusLabel = (externalJob, goneAt) => {
  const transportTypeLabel = externalJob.transport_type ? `${externalJob.transport_type}, ` : '';

  if (['canceled', 'returned'].includes(externalJob.status)) {
    const return_dropoff_at = externalJob.return_dropoff_at
      ? moment(externalJob.return_dropoff_at).format('LT')
      : '';
    const statusLabel = `returning  (${externalJob.cancel_reason}, ${externalJob.return_status}:${return_dropoff_at})`;
    return `${transportTypeLabel}${statusLabel}`;
  }

  if (!goneAt && ['pickup', 'pickup_complete'].includes(externalJob.status) && externalJob.pickup_at) {
    const pickup_at = moment(externalJob.pickup_at).format('LT');
    const imminent = externalJob.imminent ? '(imminent)' : '';
    return `${transportTypeLabel}pick_at: ${pickup_at} ${imminent}`;
  }

  if (externalJob.status === 'canceled') {
    return `${transportTypeLabel}${externalJob.status}:${externalJob.cancel_reason}`;
  }

  // Pour un job actif, afficher le statut et potentiellement les infos du coursier
  return `${transportTypeLabel}${externalJob.status}`;
};

const DockUber = ({
  dock,
  onCancelUber,
  onRegularizeUber,
  onCancelWaitingBatch,
}) => {
  // Le composant s'affiche si c'est un dock Uber ET (external_job.id OU uberdirect_batch)
  const hasExternalJob = dock.dock_type === DOCK_TYPE_UBER && !!dock.external_job.id;
  const hasBatch = dock.dock_type === DOCK_TYPE_UBER && !!dock.uberdirect_batch;

  if (!hasExternalJob && !hasBatch) {
    return (<span></span>);
  }

  const externalJobs = dock.tasks.length ? dock.tasks[0].external_shipping_jobs : null;
  const batch = dock.uberdirect_batch;

  // Détermine le statut à afficher selon la priorité batch > external_job
  const getStatus = () => {
    if (batch) {
      return getBatchStatusLabel(dock, batch);
    }
    return getExternalJobStatusLabel(dock.external_job, dock.gone_at);
  };

  const status = getStatus();

  const handleRegularizeUber = (e, dock) => {
    e.preventDefault();
    onRegularizeUber(dock);
  };

  // Détermine si on peut annuler (pas de batch, ou batch en draft)
  const canCancel = !batch && !dock.gone_at && !dock.external_job.cancel_reason;

  // Détermine si on peut régulariser (job annulé/retourné)
  const canRegularize = !batch && ['canceled', 'returned'].includes(dock.external_job.status);

  // Lien de tracking (disponible via external_job)
  const trackingUrl = dock.external_job.tracking_url;

  const canCancelBatch = batch && batch.status == "draft"

  return (
    <div>
      <span className="uber-running">
        {status}
        {externalJobs && externalJobs.some(esj => !esj.is_primary) ? <ExternalShippingJobTooltip externalJobs={externalJobs} /> : null}
        {canCancelBatch
          ? (
            <button
              type="button"
              className="btn btn-xs btn-danger"
              onClick={() => onCancelWaitingBatch(dock)}
            >
              Annuler le batch
            </button>
          ) : null
        }
        {trackingUrl
          ? (
            <a
              className="btn btn-xs btn-info"
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              tracking
            </a>
          ) : null
        }
        {canCancel
          ? (
            <button
              type="button"
              className="btn btn-xs btn-warning"
              onClick={() => onCancelUber(dock)}
            >
              annuler
            </button>
          ) : null
        }
        {canRegularize
          ? (
            <button
              type="button"
              className="btn btn-xs btn-secondary"
              onClick={(e) => handleRegularizeUber(e, dock)}>
              Régulariser
            </button>
          ) : null
        }
      </span>
    </div>
  );
};

DockUber.propTypes = {
  dock: PropTypes.object.isRequired,
  onCancelUber: PropTypes.func.isRequired,
  onCallUber: PropTypes.func,
  onRegularizeUber: PropTypes.func,
  onCancelWaitingBatch: PropTypes.func, // Annule un batch en draft
};

export default DockUber;

